import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Switch,
  TextField,
  Button,
  Divider,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Snackbar,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Dangerous as DangerousIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { authService } from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";
import { formatDateTime } from "../../utils/dateUtils";
import SudoGate from "../../components/admin/SudoGate";

// ...existing code...
const SETTINGS_KEY = "user_settings";

// Configuración por defecto
const defaultSettings = {
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  weeklyReport: true,
  eventReminders: true,
  twoFactorAuth: false,
  theme: "dark",
  language: "es",
  timezone: "America/Mexico_City",
  profileVisibility: "team",
  shareActivity: true,
};

const Settings = () => {
  const { user } = useAuth();

  // Cargar configuraciones desde localStorage
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      return saved
        ? { ...defaultSettings, ...JSON.parse(saved) }
        : defaultSettings;
    } catch {
      return defaultSettings;
    }
  };

  const [settings, setSettings] = useState(loadSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados para diálogos
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [downloadDataOpen, setDownloadDataOpen] = useState(false);

  // Estados para cambio de contraseña
  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Estados para sesiones
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Estados para mensajes
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cargar sesiones al montar
  useEffect(() => {
    loadSessions();
  }, []);

  // Detectar cambios
  useEffect(() => {
    const savedSettings = loadSettings();
    const changed = JSON.stringify(settings) !== JSON.stringify(savedSettings);
    setHasChanges(changed);
  }, [settings]);

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const data = await authService.getActiveSessions();
      if (data?.sessions) {
        setSessions(data.sessions);
      }
    } catch (err) {
      console.error("Error cargando sesiones:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleSettingChange = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSettingSelect = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // Guardar en localStorage
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));

      // Aplicar tema si cambió
      if (settings.theme === "light") {
        document.body.classList.add("light-theme");
        document.body.classList.remove("dark-theme");
      } else {
        document.body.classList.add("dark-theme");
        document.body.classList.remove("light-theme");
      }

      setHasChanges(false);
      setSnackbar({
        open: true,
        message: "Configuración guardada correctamente",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al guardar configuración",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");

    // Validaciones
    if (!passwordData.current) {
      setPasswordError("Ingresa tu contraseña actual");
      return;
    }
    if (!passwordData.new) {
      setPasswordError("Ingresa la nueva contraseña");
      return;
    }
    if (passwordData.new.length < 8) {
      setPasswordError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }

    try {
      setSavingPassword(true);
      await api.post("/auth/change-password", {
        old_password: passwordData.current,
        new_password: passwordData.new,
      });

      setChangePasswordOpen(false);
      setPasswordData({ current: "", new: "", confirm: "" });
      setSnackbar({
        open: true,
        message: "Contraseña actualizada correctamente",
        severity: "success",
      });
    } catch (err) {
      const message =
        err.response?.data?.message || "Error al cambiar contraseña";
      setPasswordError(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCloseSession = async (sessionId) => {
    try {
      await authService.revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setSnackbar({
        open: true,
        message: "Sesión cerrada correctamente",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al cerrar sesión",
        severity: "error",
      });
    }
  };

  const handleDownloadData = () => {
    const userData = {
      perfil: {
        nombre: user?.nombre_completo,
        email: user?.email,
        username: user?.username,
        rol: user?.rol,
      },
      configuracion: settings,
      exportado: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(userData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mis_datos_${user?.username}_${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

    setDownloadDataOpen(false);
    setSnackbar({
      open: true,
      message: "Datos descargados correctamente",
      severity: "success",
    });
  };

  const handleResetSettings = () => {
    setSettings(defaultSettings);
    setSnackbar({
      open: true,
      message: "Configuración restablecida a valores por defecto",
      severity: "info",
    });
  };

  // Usar utilidad centralizada de fechas con zona horaria CDMX
  const formatDate = formatDateTime;

  // Obtener token actual para identificar sesión actual
  const currentToken = localStorage.getItem("token");

  return (
    <SudoGate>
    <Box sx={{ p: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          ⚙️ Configuración
        </Typography>
        {hasChanges && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{
              background: "linear-gradient(45deg, #00C9FF 0%, #92FE9D 100%)",
              color: "#000",
              fontWeight: "bold",
            }}
          >
            Guardar Cambios
          </Button>
        )}
      </Box>

      {/* Sección 1: Notificaciones */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <NotificationsIcon sx={{ fontSize: 32, mr: 2, color: "#00C9FF" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Notificaciones
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={() => handleSettingChange("emailNotifications")}
                  color="primary"
                />
              }
              label="Notificaciones por Email"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Recibe actualizaciones importantes vía email
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smsNotifications}
                  onChange={() => handleSettingChange("smsNotifications")}
                  color="primary"
                />
              }
              label="Notificaciones por SMS"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Alertas urgentes por mensaje de texto
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.pushNotifications}
                  onChange={() => handleSettingChange("pushNotifications")}
                  color="primary"
                />
              }
              label="Notificaciones Push"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Alertas en tiempo real en el navegador
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.eventReminders}
                  onChange={() => handleSettingChange("eventReminders")}
                  color="primary"
                />
              }
              label="Recordatorios de Eventos"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Notificaciones para eventos próximos
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.weeklyReport}
                  onChange={() => handleSettingChange("weeklyReport")}
                  color="primary"
                />
              }
              label="Reporte Semanal"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Resumen semanal de tu actividad
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Sección 2: Seguridad */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <SecurityIcon sx={{ fontSize: 32, mr: 2, color: "#92FE9D" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Seguridad
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.twoFactorAuth}
                  onChange={() => handleSettingChange("twoFactorAuth")}
                  color="primary"
                />
              }
              label="Autenticación de Dos Factores (2FA)"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ ml: 4, mt: 1 }}
            >
              Protege tu cuenta con una capa adicional de seguridad
              (próximamente)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
                mt: 2,
              }}
            >
              Cambiar Contraseña
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 2, mt: 2 }}
            >
              🔐 Sesiones Activas
            </Typography>
            {loadingSessions ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : sessions.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No hay sesiones activas
              </Typography>
            ) : (
              <List>
                {sessions.map((session) => {
                  const isCurrentSession =
                    session.token === currentToken || session.is_current;
                  return (
                    <ListItem
                      key={session.id}
                      sx={{
                        backgroundColor: isCurrentSession
                          ? "rgba(46, 125, 50, 0.1)"
                          : "rgba(0,0,0,0.02)",
                        mb: 1,
                        borderRadius: 1,
                        border: isCurrentSession ? "1px solid #2e7d32" : "none",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>
                              {session.device_type === "mobile" ? "📱" : "🖥️"}
                            </span>
                            <span>
                              {session.browser} en{" "}
                              {session.device_name || session.os}
                            </span>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              IP: {session.ip_address} • Última actividad:{" "}
                              {formatDate(session.last_activity)}
                            </Typography>
                            {isCurrentSession && (
                              <Chip
                                label="Sesión Actual"
                                size="small"
                                color="success"
                                sx={{ ml: 1, mt: 0.5 }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        {!isCurrentSession && (
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleCloseSession(session.id)}
                            title="Cerrar esta sesión"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Sección 3: Preferencias */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <PaletteIcon sx={{ fontSize: 32, mr: 2, color: "#FF6B9D" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Preferencias
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Tema</FormLabel>
              <Select
                value={settings.theme}
                onChange={(e) => handleSettingSelect("theme", e.target.value)}
              >
                <MenuItem value="dark">🌙 Oscuro</MenuItem>
                <MenuItem value="light">☀️ Claro</MenuItem>
                <MenuItem value="auto">🔄 Automático</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Idioma</FormLabel>
              <Select
                value={settings.language}
                onChange={(e) =>
                  handleSettingSelect("language", e.target.value)
                }
              >
                <MenuItem value="es">🇲🇽 Español</MenuItem>
                <MenuItem value="en">🇺🇸 English</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Zona Horaria</FormLabel>
              <Select
                value={settings.timezone}
                onChange={(e) =>
                  handleSettingSelect("timezone", e.target.value)
                }
              >
                <MenuItem value="America/Mexico_City">
                  🇲🇽 México (CDMX)
                </MenuItem>
                <MenuItem value="America/Monterrey">🇲🇽 Monterrey</MenuItem>
                <MenuItem value="America/Tijuana">🇲🇽 Tijuana</MenuItem>
                <MenuItem value="America/New_York">🇺🇸 Nueva York</MenuItem>
                <MenuItem value="America/Los_Angeles">🇺🇸 Los Ángeles</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Sección 4: Privacidad */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <VisibilityIcon sx={{ fontSize: 32, mr: 2, color: "#FFA500" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Privacidad
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <FormControl component="fieldset" fullWidth>
          <FormLabel sx={{ mb: 2 }}>Visibilidad de Perfil</FormLabel>
          <RadioGroup
            value={settings.profileVisibility}
            onChange={(e) =>
              handleSettingSelect("profileVisibility", e.target.value)
            }
          >
            <FormControlLabel
              value="public"
              control={<Radio />}
              label="Público - Visible para todos"
            />
            <FormControlLabel
              value="private"
              control={<Radio />}
              label="Privado - Solo para administradores"
            />
            <FormControlLabel
              value="team"
              control={<Radio />}
              label="Equipo - Visible para colaboradores"
            />
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={settings.shareActivity}
              onChange={() => handleSettingChange("shareActivity")}
              color="primary"
            />
          }
          label="Compartir Actividad"
        />
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          sx={{ ml: 4, mt: 1 }}
        >
          Permite que otros vean tu actividad en eventos
        </Typography>
      </Paper>

      {/* Sección 5: Zona de Peligro */}
      <Paper sx={{ p: 3, border: "2px solid #c62828", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <DangerousIcon sx={{ fontSize: 32, mr: 2, color: "#c62828" }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#c62828" }}>
            Zona de Peligro
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Estas acciones pueden afectar tu cuenta. Procede con cuidado.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RefreshIcon />}
              fullWidth
              onClick={handleResetSettings}
            >
              Restablecer Configuración
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<DownloadIcon />}
              fullWidth
              onClick={() => setDownloadDataOpen(true)}
            >
              Descargar Mis Datos
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              fullWidth
              onClick={() => setDeleteAccountOpen(true)}
            >
              Eliminar Cuenta
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialog: Cambiar Contraseña */}
      <Dialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🔐 Cambiar Contraseña</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            type={showCurrentPassword ? "text" : "password"}
            label="Contraseña Actual"
            value={passwordData.current}
            onChange={(e) =>
              setPasswordData({ ...passwordData, current: e.target.value })
            }
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={showNewPassword ? "text" : "password"}
            label="Nueva Contraseña"
            value={passwordData.new}
            onChange={(e) =>
              setPasswordData({ ...passwordData, new: e.target.value })
            }
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={showNewPassword ? "text" : "password"}
            label="Confirmar Nueva Contraseña"
            value={passwordData.confirm}
            onChange={(e) =>
              setPasswordData({ ...passwordData, confirm: e.target.value })
            }
            margin="normal"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            La contraseña debe tener al menos 8 caracteres.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={savingPassword}
            startIcon={
              savingPassword ? <CircularProgress size={20} /> : <SecurityIcon />
            }
          >
            {savingPassword ? "Cambiando..." : "Cambiar Contraseña"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Descargar Datos */}
      <Dialog
        open={downloadDataOpen}
        onClose={() => setDownloadDataOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>📥 Descargar Mis Datos</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Se descargará un archivo JSON con tu información:
          </Typography>
          <List dense>
            <ListItem>• Información de perfil</ListItem>
            <ListItem>• Configuración actual</ListItem>
            <ListItem>• Fecha de exportación</ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDataOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleDownloadData}
            startIcon={<DownloadIcon />}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Eliminar Cuenta */}
      <Dialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          ⚠️ Eliminar Cuenta
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Esta acción es IRREVERSIBLE. Tu cuenta y todos tus datos serán
            eliminados permanentemente.
          </Alert>
          <Typography>
            Para eliminar tu cuenta, por favor contacta al administrador del
            sistema.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteAccountOpen(false)}
            variant="contained"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </SudoGate>
  );
};

export default Settings;
